<template>
    <div class="mx-4">
        <div v-if="manual_mode">
            <div class="my-2" v-if="selected_actuals.length == 0"><br /></div>
            <div v-else class="d-flex align-center ga-2 my-2">
                <span class="text-caption text-medium-emphasis">
                    {{ t("kit_selected_count").replace("{n}", selected_actuals.length) }}
                </span>
                <v-spacer />
                <ButtonSetAsReady :actuals="selected_actuals" />
            </div>
        </div>
        <v-row>
            <SelectorProductionDate class="mx-auto mb-2" :wide="true" />
        </v-row>
        <ButtonSelectionFromFinalReport @selected="selected_actuals = $event" />
        <v-card-subtitle v-if="other_date_selection_count">
            {{ t("selected_on_other_dates").replace("{n}", other_date_selection_count) }}
            <v-btn variant="text" color="accent" size="small" @click="selected_actuals = []">
                {{ t("clear") }}</v-btn>
        </v-card-subtitle>
        <v-treeview color="accent" open-all class="ma-3" item-disabled="locked" density="compact" selectable
            select-strategy="auto" item-value="id" v-model:selected="selected_actuals" :items="actuals_tree">
            <template v-slot:label="{ item }">
                <span class="text-grey" v-if="item.ready || item.kitted"> {{ item.title }} </span>
                <span v-else> {{ item.title }} </span>
            </template>
            <template v-slot:append="{ item }">
                <v-chip variant="outlined" size="x-small" v-if="item.ready" color="green">
                    {{ t("ready") }}
                </v-chip>
                <template v-else-if="kit_on && item.kit">
                    <v-chip variant="outlined" size="x-small" :color="item.kit.color" class="pit-mono">
                        {{ item.kit.text }}
                    </v-chip>
                    <v-btn v-if="manual_mode && !item.kitted" icon="mdi-check" size="x-small" variant="text"
                        color="success" class="ml-1" :title="t('kit_mark')" @click.stop="markOne(item)" />
                </template>
            </template>
        </v-treeview>
    </div>
</template>

<script setup>
import _ from "lodash"
import { nameSort } from "/src/utils/basics.js"
import ButtonSetAsReady from "../../components/left_menu/ButtonSetAsReady.vue";
import SelectorProductionDate from "./SelectorProductionDate.vue";

import { usePitStoreBasics } from "@/stores/app_common";
import { useSessionStore } from "@/stores/session";
import { useSelectedLogisticsStore } from "@/stores/selected_logistics";
import { useConstructiblesStore } from "@/stores/constructibles";
import { useSelectedStore } from "@/stores/selected";
import { ref, watch, computed } from "vue";
import ButtonSelectionFromFinalReport from "../logistics_viewer/ButtonSelectionFromFinalReport.vue"
import { kitFraction, isKitted } from "/src/utils/kits.js"
import { markKits } from "/src/utils/mark_kits.js"

// Data
const selected_actuals = ref([]);
const loading = ref(false);
const by_priority = ref(true);

// Stores
const pitStoreBasics = usePitStoreBasics();
const { t } = pitStoreBasics;
const constructibles_store = useConstructiblesStore();
const selected_logistics_store = useSelectedLogisticsStore();
const sessionStore = useSessionStore();
const selectedStore = useSelectedStore();

// Computed
const manual_mode = computed(() => sessionStore.kit_mode === 'manual');
const kit_on = computed(() => sessionStore.kit_tracking_on);
const date = computed(() => selectedStore.selected_date);
const actuals = computed(() => constructibles_store.annotatedActualsFromLevel("all"));
const other_date_selection_count = computed(() => {
    let selected_length = selected_actuals.value.length;
    let intersection_length = _.intersection(selected_actuals.value, actuals.value.map(a => a.uuid)).length;
    return selected_length - intersection_length;
})

const actuals_tree = computed(() => {
    let actuals_tree = [];

    if (actuals.value.length > 0) {
        let children_actuals = actuals.value.map(actual => {
            let design_name = constructibles_store.designNameByUuid(actual.design, false);
            return {
                kitted: isKitted(actual),
                kit: kit_on.value ? kitFraction(actual, t) : null,
                ready: actual.status === "ready",
                id: actual.uuid,
                design_name: design_name,
                priority: actual.priority,
                title: `${design_name} [${actual.uuid.substring(0, 8)}]`,
            };
        });

        let sorted_children;
        if (by_priority.value) {
            sorted_children = _.orderBy(children_actuals, "priority");
        } else {
            sorted_children = children_actuals.sort((a, b) => nameSort(a.design_name, b.design_name));
        }

        actuals_tree.push({
            id: date.value,
            title: date.value,
            locked: sorted_children.length === 0,
            children: sorted_children,
        });
    }

    if (loading.value) {
        return [{ id: 0, title: "Loading...", locked: true }];
    } else if (actuals_tree.length === 0) {
        return [{ id: 0, title: "No constructibles.", locked: true }];
    } else {
        return actuals_tree;
    }
});


watch(selected_actuals, (sa) => {
    selected_logistics_store.setSelectedActuals(sa);
});

/** One tap on a row: the per-panel half of the manual mode. */
function markOne(item) {
    return markKits([item.id], true, t);
}

</script>
